import { useRef, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Pill } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { useFeedback } from "../components/Feedback";
import { IssueList } from "../components/IssueList";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import { api, type Issue } from "../lib/api";
import { day, money, num, titleCase, when } from "../lib/format";
import { useApi } from "../lib/useApi";

export interface SourceRow {
  code: string; name: string; connector: string; location: string | null; location_name: string | null;
  expected_frequency_hours: number | null; last_ingest: string | null; as_of: string | null; age_days: number | null;
  stale: boolean; rows: number | null; reconciles: boolean | null; can_sync: boolean; runs: number;
  last_checked_at: string | null; last_check_error: string | null;
  mode: "live" | "file" | "pending"; how: string | null; blocked_on: string | null; owner: string | null;
  impact: string | null; upgrade: string | null; note: string | null; url: string | null;
}
interface Run { id: number; status: string; file_name: string | null; as_of: string | null; started_at: string; rows: number | null; stated_total: number | null; parsed_total: number | null; reconciles: boolean | null; error: string | null }

export function Reconcile({ value }: { value: boolean | null }) {
  if (value === true) return <Pill tone="good">✓ reconciles</Pill>;
  if (value === false) return <Pill tone="error">✕ mismatch</Pill>;
  return <span className="muted">no stated total</span>;
}

function useSync(onDone: () => void) {
  const { toast } = useFeedback();
  const [busy, setBusy] = useState<string | null>(null);
  const run = async (code: string, action: "sync" | "fetch") => {
    setBusy(code);
    try {
      const r = await api.post<{ results: { file: string; skipped: boolean; rows: number }[];
                                 files?: string[]; fetch_errors?: string[]; message?: string }>(`/sources/${code}/${action}`);
      if (r.message) { toast(r.message); onDone(); return; }
      const fresh = r.results.filter((x) => !x.skipped);
      // A fetch that reached the portal but came back empty is worth saying out
      // loud: "no new data" would read as "all fine" when it is not.
      if (r.fetch_errors?.length) toast(`Fetched with problems: ${r.fetch_errors.join("; ")}`, "error");
      else if (fresh.length) toast(`Imported ${fresh.map((x) => `${x.file} (${x.rows} rows)`).join(", ")}`);
      else if (action === "fetch") toast(`Pulled ${r.files?.length ?? 0} file(s) — nothing in them had changed`);
      else toast("No new data — the latest file is already imported");
      onDone();
    } catch (e) { toast((e as Error).message, "error"); } finally { setBusy(null); }
  };
  return { sync: (c: string) => run(c, "sync"), fetch: (c: string) => run(c, "fetch"), busy };
}

const MODES = {
  live: { label: "Connected", tone: "good",
          blurb: "The app fetches these itself on a schedule. Nobody has to send anything." },
  file: { label: "Sent as files", tone: "info",
          blurb: "Someone still maintains these by hand. Upload the file here and it is imported straight away — "
                 + "or move the sheet to Google Sheets and it reads itself, like BCI." },
  pending: { label: "Waiting on access", tone: "warning",
             blurb: "Not connected yet. Each says what is in the way and who it is with." },
} as const;

export function Sources() {
  const { data, error, reload } = useApi<SourceRow[]>("/sources");
  const uploads = useApi<Uploadable[]>("/uploads");
  const { sync, fetch: fetchNow, busy } = useSync(reload);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  const counts = (m: string) => (data ?? []).filter((s) => s.mode === m).length;

  return (
    <>
      <PageHeader title="Integrations"
        sub="Every source of data, whether it is connected, and what is holding up the ones that are not. Every connection is read-only — the app never writes to anyone's system." />
      {!data ? <Loading /> : (
        <>
          <section className="tiles" aria-label="Integration summary">
            {(["live", "file", "pending"] as const).map((m) => (
              <div className="tile" key={m}>
                <span className="tile-label">{MODES[m].label}</span>
                <span className={`tile-value${m === "pending" && counts(m) ? " is-warning" : ""}`}>{counts(m)}</span>
                <span className="tile-sub">{m === "live" ? "fetched automatically" : m === "file" ? "sent by hand" : "blocked on someone"}</span>
              </div>
            ))}
          </section>
          {(["live", "file", "pending"] as const).map((m) => (
            <Card key={m} title={<>{MODES[m].label} <Pill tone={MODES[m].tone}>{counts(m)}</Pill></>}>
              <p className="note muted" style={{ marginTop: 0 }}>{MODES[m].blurb}</p>
              <div className="integration-list" data-testid={`integrations-${m}`}>
                {(data ?? []).filter((s) => s.mode === m).map((s) => (
                  <IntegrationCard key={s.code} s={s} onSync={sync} onFetch={fetchNow} busy={busy} onUploaded={reload}
                                   upload={(uploads.data ?? []).find((u) => u.code === s.code)} />
                ))}
              </div>
            </Card>
          ))}
        </>
      )}
    </>
  );
}

interface Uploadable { code: string; name: string; accepts: string[]; label: string; template: string | null }

/** Send a file straight from the browser, for the sources someone still
 *  maintains by hand. It is imported on arrival, so the result comes back with
 *  the row count and whether the file's own totals agreed. */
function UploadButton({ spec, onDone }: { spec: Uploadable; onDone: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useFeedback();

  const send = async (file: File) => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await fetch(`/api/sources/${spec.code}/upload`, { method: "POST", body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail ?? `Upload failed (${res.status})`);
      toast(data.skipped
        ? `${file.name}: already imported, nothing changed`
        : `${file.name}: ${data.rows} rows imported${data.reconciles === false ? " — totals do not agree, see Data quality" : ""}`,
        data.reconciles === false ? "error" : "good");
      onDone();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <>
      <input ref={input} type="file" accept={spec.accepts.join(",")} className="visually-hidden"
             aria-label={`Upload a file for ${spec.name}`}
             onChange={(e) => { const f = e.target.files?.[0]; if (f) void send(f); }} />
      <button className="btn btn-sm" disabled={busy} onClick={() => input.current?.click()}
              title={`${spec.label} (${spec.accepts.join(", ")})`}>
        {busy ? "Importing…" : "Upload file"}
      </button>
      {spec.template && (
        <a className="card-link" href={`/api/uploads/template/${spec.template}`} download
           title="An example with the right columns, filled in">template</a>
      )}
    </>
  );
}

const FETCHABLE = ["MHW_INVENTORY", "MHW_SALES", "OPICI_DEPLETIONS", "OPICI_STOCK"];
const portalOf = (code: string) => (code.startsWith("OPICI") ? "OPICI's DivePort" : "MHW's portal");

function IntegrationCard({ s, onSync, onFetch, busy, upload, onUploaded }: {
  s: SourceRow; onSync: (code: string) => void; onFetch: (code: string) => void; busy: string | null;
  upload?: Uploadable; onUploaded: () => void;
}) {
  return (
    <article className="integration" data-testid="integration">
      <div className="integration-head">
        <h3><Link to={`/sources/${s.code}`}>{s.name}</Link></h3>
        <div className="integration-actions">
          {s.location && <Link className="muted" to={`/locations/${s.location}`}>{s.location_name}</Link>}
          {upload && <UploadButton spec={upload} onDone={onUploaded} />}
          {FETCHABLE.includes(s.code) && (
            <button className="btn btn-sm btn-primary" disabled={busy !== null}
              onClick={() => onFetch(s.code)} aria-label={`Fetch ${s.name} from the portal now`}
              title={`Sign in to ${portalOf(s.code)} and pull today's figures now`}>
              {busy === s.code ? "Fetching…" : "Fetch now"}
            </button>
          )}
          {s.can_sync && (
            <button className="btn btn-sm" disabled={busy !== null} onClick={() => onSync(s.code)} aria-label={`Sync ${s.name}`}>
              {busy === s.code ? "Syncing…" : "Sync now"}
            </button>
          )}
        </div>
      </div>
      {s.how && <p className="integration-how">{s.how}</p>}

      {s.mode === "pending" ? (
        <dl className="facts">
          {s.blocked_on && <div><dt>Waiting on</dt><dd>{s.blocked_on}</dd></div>}
          {s.owner && <div><dt>With</dt><dd>{s.owner}</dd></div>}
          {s.impact && <div><dt>What it unblocks</dt><dd>{s.impact}</dd></div>}
        </dl>
      ) : (
        <dl className="facts">
          <div><dt>Data as of</dt><dd>{s.as_of ? <>{day(s.as_of)}{s.stale && <> <Pill tone="warning">stale</Pill></>}</> : <span className="muted">never imported</span>}</dd></div>
          <div><dt>Last checked</dt><dd>{s.last_checked_at ? when(s.last_checked_at) : <span className="muted">not yet</span>}</dd></div>
          <div><dt>Rows</dt><dd>{num(s.rows)}</dd></div>
          <div><dt>Totals</dt><dd><Reconcile value={s.reconciles} /></dd></div>
        </dl>
      )}

      {s.last_check_error && (
        <p className="integration-error" role="alert">Last check failed: {s.last_check_error}</p>
      )}
      {s.upgrade && <p className="integration-note"><strong>Could be better:</strong> {s.upgrade}</p>}
      {s.note && <p className="integration-note muted">{s.note}</p>}
    </article>
  );
}

export function SourceDetail() {
  const { code } = useParams();
  const { data, error, reload } = useApi<SourceRow & { config: Record<string, unknown>; runs: Run[] }>(`/sources/${code}`);
  const { sync, fetch: fetchNow, busy } = useSync(reload);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return (
    <>
      <PageHeader crumbs={[{ label: "Integrations", to: "/sources" }]} title={data.name}
        badge={<Pill tone={MODES[data.mode]?.tone ?? "neutral"}>{MODES[data.mode]?.label ?? data.mode}</Pill>}
        sub={data.how}
        actions={<>
          {FETCHABLE.includes(data.code) && (
            <button className="btn btn-primary" disabled={busy !== null}
              onClick={() => fetchNow(data.code)}
              title={`Sign in to ${portalOf(data.code)} and pull today's figures now`}>
              {busy ? "Fetching…" : "Fetch now"}
            </button>)}
          {data.can_sync && (
            <button className="btn" disabled={busy !== null} onClick={() => sync(data.code)}>
              {busy ? "Syncing…" : "Re-read the file"}
            </button>)}
        </>} />
      <Card>
        <Facts items={[["Code", <code>{data.code}</code>], ["Connector", data.connector],
          ["Location", data.location ? <Link to={`/locations/${data.location}`}>{data.location}</Link> : null],
          ["Checked every", data.expected_frequency_hours ? `${data.expected_frequency_hours} hour(s)` : null],
          ["Last checked", data.last_checked_at ? when(data.last_checked_at) : null],
          ["Waiting on", data.blocked_on], ["With", data.owner],
          ["Source", data.url ? <a href={data.url} target="_blank" rel="noreferrer">open the original ↗</a> : null],
          ...Object.entries(data.config).map(([k, v]) => [titleCase(k), String(v)] as [string, string])]} />
        {data.last_check_error && <p className="integration-error" role="alert">Last check failed: {data.last_check_error}</p>}
      </Card>
      <Card title="Imports">
        <DataTable<Run> rows={data.runs} rowKey={(r) => r.id} href={(r) => `/imports/${r.id}`} filterPlaceholder={false} columns={[
          { key: "id", label: "#", render: (r) => <Link to={`/imports/${r.id}`}>#{r.id}</Link> },
          { key: "status", label: "Status", render: (r) => <Pill tone={r.status === "ok" ? "good" : "error"}>{r.status}</Pill> },
          { key: "file_name", label: "File" }, { key: "as_of", label: "Data as of", render: (r) => day(r.as_of) },
          { key: "started_at", label: "Imported", render: (r) => when(r.started_at) },
          { key: "rows", label: "Rows", numeric: true }, { key: "reconciles", label: "Checked", render: (r) => <Reconcile value={r.reconciles} /> },
        ]} />
      </Card>
    </>
  );
}

interface RunDetailT extends Run {
  source: string; source_name: string; counts: { stock: number; sales: number }; kinds: Record<string, number>;
  raw_total: number; offset: number; limit: number; raw: { seq: number; kind: string; payload: Record<string, unknown> }[];
  issues: Issue[]; unmatched: { code: string | null; text: string | null; occurrences: number }[];
}

export function RunDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") ?? "";
  const offset = Number(params.get("offset") ?? 0);
  const { data, error, reload } = useApi<RunDetailT>(`/runs/${id}?offset=${offset}&limit=100${kind ? `&kind=${kind}` : ""}`);
  const tabs = [
    { id: "raw", label: "Raw rows", count: data?.raw_total },
    { id: "issues", label: "Issues", count: data?.issues.length },
    { id: "unmatched", label: "Unmatched", count: data?.unmatched.length },
  ];
  const [tab, setTab] = useTab(tabs);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const setP = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== "offset") n.delete("offset"); setParams(n, { replace: true }); };
  const cols = Array.from(new Set(data.raw.flatMap((r) => Object.keys(r.payload)))).slice(0, 12);
  return (
    <>
      <PageHeader crumbs={[{ label: "Sources & imports", to: "/sources" }, { label: data.source_name, to: `/sources/${data.source}` }]}
        title={`Import #${data.id}`} sub={data.file_name} />
      <Card>
        <Facts items={[["Status", data.status], ["Data as of", day(data.as_of)], ["Imported", when(data.started_at)], ["Rows parsed", num(data.rows)],
          ["Stock rows", num(data.counts.stock)], ["Sales rows", num(data.counts.sales)],
          ["Total printed on source", data.stated_total == null ? "—" : money(data.stated_total)],
          ["Total we parsed", data.parsed_total == null ? "—" : money(data.parsed_total)], ["Checked", <Reconcile value={data.reconciles} />],
          ...(data.error ? [["Error", <span className="bad">{data.error}</span>] as [string, ReactNode]] : [])]} />
      </Card>
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {tab === "raw" && (
        <Card actions={
          <select aria-label="Row kind" value={kind} onChange={(e) => setP("kind", e.target.value)}>
            <option value="">All kinds</option>
            {Object.entries(data.kinds).map(([k, n]) => <option key={k} value={k}>{k} ({n})</option>)}
          </select>}>
          <p className="note muted">Every row exactly as the source gave it. Showing {data.raw.length ? offset + 1 : 0}–{offset + data.raw.length} of {data.raw_total}.</p>
          <div className="table-scroll">
            <table className="grid compact mono" data-testid="raw-table">
              <thead><tr><th>#</th><th>Kind</th>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {data.raw.map((r) => (
                  <tr key={r.seq}><td>{r.seq}</td><td>{r.kind}</td>
                    {cols.map((c) => <td key={c}>{typeof r.payload[c] === "object" && r.payload[c] ? JSON.stringify(r.payload[c]) : String(r.payload[c] ?? "")}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <button className="btn btn-sm" disabled={offset === 0} onClick={() => setP("offset", String(Math.max(0, offset - 100)))}>← Previous</button>
            <button className="btn btn-sm" disabled={offset + 100 >= data.raw_total} onClick={() => setP("offset", String(offset + 100))}>Next →</button>
          </div>
        </Card>
      )}
      {tab === "issues" && <Card><IssueList issues={data.issues} /></Card>}
      {tab === "unmatched" && (
        <Card>
          <DataTable rows={data.unmatched} rowKey={(u) => `${u.code}-${u.text}`} filterPlaceholder={false} empty="Every row matched a product."
            columns={[{ key: "code", label: "Code" }, { key: "text", label: "Text" }, { key: "occurrences", label: "Rows", numeric: true }]} />
        </Card>
      )}
    </>
  );
}
